import type { EventEnvelope, EventType } from '@inbx/shared/events';
import { io, type Socket } from 'socket.io-client';

import { apiClient } from './client.js';

export type StatusDuration =
  | 'session'
  | '1m'
  | '15m'
  | '30m'
  | '45m'
  | '1h'
  | '3h'
  | 'persistent';

export interface RealtimeClient {
  connect(): Promise<void>;
  disconnect(): void;
  on<T = unknown>(event: EventType, handler: (envelope: EventEnvelope) => void): () => void;
  /** Срабатывает при каждом (пере)подключении сокета — для добора пропущенных событий. */
  onConnect(handler: () => void): () => void;
  emitTyping(ticketId: string, isTyping: boolean): void;
  subscribeTicket(ticketId: string): Promise<void>;
  unsubscribeTicket(ticketId: string): Promise<void>;
  setMyStatus(
    status: 'online' | 'busy' | 'away',
    duration?: StatusDuration,
  ): Promise<{ ok: boolean; effective?: string; error?: string }>;
}

class RealtimeClientImpl implements RealtimeClient {
  private socket: Socket | null = null;
  private connecting: Promise<void> | null = null;

  async connect(): Promise<void> {
    if (this.socket?.connected) return;
    // Mutex: React.StrictMode в dev монтирует useEffect дважды → две параллельных
    // connect() без локa создавали два разных io()-соединения (отсюда 2 sid у
    // одной вкладки). Шарим один pending-promise.
    if (this.connecting) return this.connecting;
    this.connecting = (async () => {
      try {
        const { token } = await apiClient.get<{ token: string }>('/realtime/token');
        // WS приоритет, polling — fallback для сетей где WS блокирован
        // (корпоративные прокси). Без fallback presence просто не подключится.
        this.socket = io({
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
        });
        // Сервер периодически шлёт `ticket.assign-probe` чтобы проверить что
        // оператор реально на линии перед назначением тикета. Отвечаем синхронно
        // ack-ом — наличие коннекта = жив. Если бы не ответили — сервер бы
        // считал нас умершим и назначил тикет следующему оператору.
        this.socket.on(
          'ticket.assign-probe',
          (_body: unknown, ack?: (resp: { ok: true }) => void) => {
            if (typeof ack === 'function') ack({ ok: true });
          },
        );
        await new Promise<void>((resolve, reject) => {
          const onConnect = (): void => {
            cleanup();
            resolve();
          };
          const onError = (err: Error): void => {
            cleanup();
            reject(err);
          };
          const cleanup = (): void => {
            this.socket?.off('connect', onConnect);
            this.socket?.off('connect_error', onError);
          };
          this.socket?.once('connect', onConnect);
          this.socket?.once('connect_error', onError);
        });
      } finally {
        this.connecting = null;
      }
    })();
    return this.connecting;
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  on<T = unknown>(event: EventType, handler: (envelope: EventEnvelope) => void): () => void {
    if (!this.socket) throw new Error('socket not connected');
    this.socket.on(event, handler);
    return () => this.socket?.off(event, handler);
  }

  /**
   * socket.io шлёт 'connect' и при первом подключении, и при КАЖДОМ реконнекте.
   * Используем для refetch'а данных, чтобы добрать события, пропущенные пока
   * сокет был отключён (одноразовый broadcast реплея не имеет).
   */
  onConnect(handler: () => void): () => void {
    if (!this.socket) throw new Error('socket not connected');
    this.socket.on('connect', handler);
    return () => this.socket?.off('connect', handler);
  }

  emitTyping(ticketId: string, isTyping: boolean): void {
    this.socket?.emit('typing', { ticket_id: ticketId, is_typing: isTyping });
  }

  async subscribeTicket(ticketId: string): Promise<void> {
    await this.socket?.emitWithAck('ticket.subscribe', { ticket_id: ticketId });
  }

  async unsubscribeTicket(ticketId: string): Promise<void> {
    await this.socket?.emitWithAck('ticket.unsubscribe', { ticket_id: ticketId });
  }

  /**
   * Ручная смена статуса (online/busy/away) + длительность. Идёт через WS,
   * не HTTP — единый канал управления presence. Сервер ответит ack с
   * фактическим effective статусом (если, например, оператор офлайн — будет
   * 'offline'). UI применяет это сразу, локального state не держит.
   */
  async setMyStatus(
    status: 'online' | 'busy' | 'away',
    duration: StatusDuration = 'session',
  ): Promise<{ ok: boolean; effective?: string; error?: string }> {
    if (!this.socket) throw new Error('socket not connected');
    return (await this.socket.emitWithAck('status.set', { status, duration })) as {
      ok: boolean;
      effective?: string;
      error?: string;
    };
  }
}

export const realtime: RealtimeClient = new RealtimeClientImpl();
